import { Info, X, type IconProps } from '@phosphor-icons/react';
import {
  forwardRef,
  type HTMLAttributes,
  type LabelHTMLAttributes,
} from 'react';
import { cn } from '@skupreme/shared/ui';
import { AppTableRoot, type AppTableRootProps } from '@components/AppTable';
import { MuteLabel as KitMuteLabel } from '@elements/FormField';

export { CenterDropdownStyle } from '@elements/Dropdown';
export { InputContainer } from '@elements/form-field/chrome';

type DivProps = HTMLAttributes<HTMLDivElement>;

export function MuteLabel({
  className,
  ...rest
}: LabelHTMLAttributes<HTMLLabelElement>) {
  return <KitMuteLabel {...rest} className={cn('sk:block', className)} />;
}

type CustomDateFieldProps = DivProps & {
  error?: boolean;
  required?: boolean;
};

/* Keeps Semantic's `field` / `error` / `required` classes: the dynamic form's rules key on them. */
export function CustomDateField({
  className,
  error,
  required,
  ...rest
}: CustomDateFieldProps) {
  return (
    <div
      {...rest}
      className={cn(
        'field',
        error && 'error',
        required && 'required',
        'sk:[&_.react-datepicker-wrapper]:w-full sk:[&.table-input]:w-[130px]',
        className,
      )}
    />
  );
}

export function RowContainer({ className, ...rest }: DivProps) {
  return <div {...rest} className={cn('sk:flex sk:flex-col', className)} />;
}

export function GreyFont({
  className,
  ...rest
}: HTMLAttributes<HTMLSpanElement>) {
  return <span {...rest} className={cn('sk:text-fg-tertiary', className)} />;
}

type InputContainerReadOnlyProps = DivProps & { inline?: boolean };

export function InputContainerReadOnly({
  inline,
  className,
  ...rest
}: InputContainerReadOnlyProps) {
  return (
    <div
      {...rest}
      className={cn(
        inline ? 'sk:inline-flex' : 'sk:flex',
        'sk:relative sk:min-h-5.5 sk:items-center',
        'sk:[&_span]:max-w-full sk:[&_span]:overflow-hidden sk:[&_span]:text-ellipsis',
        className,
      )}
    />
  );
}

export const StyledTable = forwardRef<HTMLTableElement, AppTableRootProps>(
  function StyledTable({ className, ...rest }, ref) {
    return (
      <AppTableRoot
        {...rest}
        ref={ref}
        className={cn(
          'sk:border-none!',
          'sk:[&_th]:bg-surface! sk:[&_th]:text-xs! sk:[&_th]:font-normal! sk:[&_th]:whitespace-nowrap sk:[&_th]:text-fg-tertiary! sk:[&_th]:capitalize!',
          'sk:[&_.col-12]:p-0!',
          className,
        )}
      />
    );
  },
);

export function DeleteIcon({ className, ...rest }: IconProps) {
  return (
    <X
      {...rest}
      className={cn(
        'sk:cursor-pointer sk:text-danger sk:hover:opacity-80',
        className,
      )}
    />
  );
}

export function FlexNestedTableRow({ className, ...rest }: DivProps) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:[&.box-container]:mx-3.75 sk:[&.box-container]:h-full sk:[&.box-container]:min-h-15',
        'sk:[&.box-container]:rounded-[5px] sk:[&.box-container]:border sk:[&.box-container]:border-solid sk:[&.box-container]:border-border sk:[&.box-container]:p-1.75',
        'sk:[&.box-container_.header]:mb-1.25 sk:[&.box-container_.header]:w-full sk:[&.box-container_.header]:border-0 sk:[&.box-container_.header]:border-b sk:[&.box-container_.header]:border-solid sk:[&.box-container_.header]:border-border sk:[&.box-container_.header]:pr-3.75 sk:[&.box-container_.header]:font-bold',
        className,
      )}
    />
  );
}

export function RadioArrayContainer({ className, ...rest }: DivProps) {
  return (
    <div
      {...rest}
      className={cn('sk:mt-0.5 sk:flex sk:flex-wrap sk:gap-2.5', className)}
    />
  );
}

export const InfoIcon = forwardRef<SVGSVGElement, IconProps>(function InfoIcon(
  { className, ...rest },
  ref,
) {
  return (
    <Info
      {...rest}
      ref={ref}
      className={cn('sk:-mb-0.5 sk:ml-0.5', className)}
    />
  );
});

export function DynamicFieldsContainer({ className, ...rest }: DivProps) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:flex sk:flex-wrap sk:items-end sk:gap-y-2.5',
        'sk:[&_.primary-btn_button]:bg-primary! sk:[&_.primary-btn_button]:text-surface!',
        'sk:[&_button.primary-btn]:bg-primary! sk:[&_button.primary-btn]:text-surface!',
        'sk:[&_.fluid_button]:w-full sk:[&_.margin-0]:m-0!',
        'sk:[&_.error.required.field:hover_.dropdown]:bg-danger-bg!',
        'sk:[&_.dropdown_.ui.image]:max-w-7.5',
        'sk:[&_.has-boxes]:flex sk:[&_.has-boxes]:flex-col sk:[&_.has-boxes]:gap-y-2.5',
        'sk:[&_.item_.dropdown-icon]:-mb-1 sk:[&_.item_.dropdown-icon]:mr-1',
        className,
      )}
    />
  );
}
